import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Card, Modal, Stack, Typography, Box, Slide, IconButton } from '@mui/material';
import { DismissIcon } from '@wavetrak/theme';
import { trackEvent } from '@surfline/web-common';

import CurrentWindMap from 'components/CurrentWindMap';
import ErrorBoundary from 'components/ErrorBoundary';
import Information from 'components/Information';
import LiveWindDataVisualizer from 'components/LiveWindDataVisualizer';
import useStoredGraphToggle, {
  LINE,
} from 'components/LiveWindDataVisualizer/hooks/useStoredGraphToggle';
import useConditions from 'hooks/useConditions';
import { useUserPermissionStatus } from 'selectors/user';
import { getSpotDetails, getSpotReportAssociated } from 'selectors/spot';
import { getSubregionOverviewData } from 'selectors/subregion';
import { useAppSelector } from 'stores/hooks';
import type { GeoLocation } from 'types/locationView';
import type { SpotReportAssociated } from 'types/spot';
import LiveWindSourceAttribution, {
  LiveWindAttributionSources,
} from 'components/LiveWindSourceAttribution/LiveWindSourceAttribution';
import useFetchLiveWind from './hooks/useFetchLiveWind';
import ModalSubHeader from './ModalSubHeader';
import LiveWindModalError from './LiveWindModalError';

import styles from './LiveWindModal.module.scss';

export interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  geo: GeoLocation;
  paywall?: React.ReactElement;
  spotId: string;
  // For testing/storybook only
  forceLoading?: boolean;
}

const LiveWindModal: React.FC<Props> = ({ open, setOpen, geo, paywall, spotId, forceLoading }) => {
  const { hasLiveWindPermissions } = useUserPermissionStatus();
  const [windDirection, setWindDirection] = useState(0);
  const { graph } = useStoredGraphToggle();
  const spot = useAppSelector(getSpotDetails);
  const spotAssociated: SpotReportAssociated = useAppSelector(getSpotReportAssociated);
  const subregionOverview = useAppSelector(getSubregionOverviewData);
  const { themeClassName } = useConditions();

  const { data, error, loading } = useFetchLiveWind(spotId, !hasLiveWindPermissions || !open);
  const isLoading = forceLoading || loading;

  const closeModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      trackEvent('Modal Opened', {
        modalType: 'live-wind',
        graphType: graph === LINE ? 'line' : 'bar',
        spotName: spot?.name,
        spotId,
        subregionId: spot?.subregion?._id,
        subregionName: subregionOverview?.name,
      });
    }
    // We only want to run the tracking code when the dialog is opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const tutorialSegmentProps = {
    spotName: spot?.name,
    spotId,
    subregionId: spot?.subregion?._id,
    subregionName: subregionOverview?.name,
    modalType: 'tutorial',
    tabName: 'report',
    uiElement: 'live-wind',
  };

  return (
    <Modal
      keepMounted
      open={open}
      onClose={closeModal}
      aria-labelledby="live-wind-modal-title"
      aria-describedby="live-wind-modal-description"
    >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Box className={classNames(styles.cardWrapper, themeClassName)}>
          <Card
            variant="modal"
            className={classNames({
              [styles.card]: true,
              [styles.cardWithPaywall]: !hasLiveWindPermissions,
            })}
            data-testid="live-wind-modal"
          >
            <IconButton
              className={styles.dismissIcon}
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={closeModal}
            >
              <DismissIcon />
            </IconButton>
            <ErrorBoundary
              error={error}
              errorBoundaryId="live-wind-modal"
              render={() => <LiveWindModalError />}
            >
              {!hasLiveWindPermissions && !!paywall ? (
                paywall
              ) : (
                <Stack direction="column" justifyContent="center" alignItems="center" width="100%">
                  <Stack direction="column" justifyContent="center" alignItems="flex-start">
                    <Stack direction="row" spacing={0} alignItems="center">
                      <Typography id="live-wind-modal-title" variant="h4" component="h1">
                        Live Wind
                      </Typography>
                      <Information
                        articleId="4swNoFwlOlX5vLc2z5EJt7"
                        segmentProps={tutorialSegmentProps}
                        tooltip="Live Wind"
                        tutorialId="7KyiikmTf3WBwvUGMnq4kP"
                      />
                    </Stack>
                    <ModalSubHeader
                      location={data?.associated.windStation.name}
                      geo={geo}
                      loading={isLoading}
                      windStationLocation={data?.associated.windStation.location}
                    />
                    <Box className={styles.map} data-testid="map-container">
                      <CurrentWindMap
                        size="large"
                        geo={geo}
                        windDirection={windDirection}
                        isLiveAvailable
                        isLoading={isLoading || error}
                        changeAnimation
                        loopAnimation={false}
                        withSkeleton
                      />
                    </Box>
                  </Stack>
                  <LiveWindDataVisualizer
                    liveWind={data}
                    isLoading={isLoading}
                    setWindDirection={setWindDirection}
                  />
                  {spotAssociated.windStation?.provider &&
                    spotAssociated.windStation?.provider !== 'DAVIS' && (
                      <Box className={styles.attribution}>
                        <LiveWindSourceAttribution
                          source={spotAssociated.windStation.provider as LiveWindAttributionSources}
                        />
                      </Box>
                    )}
                </Stack>
              )}
            </ErrorBoundary>
          </Card>
        </Box>
      </Slide>
    </Modal>
  );
};

export default LiveWindModal;
